/* eslint-disable react/require-default-props */
import { Grid, MenuItem, TextFieldProps } from '@mui/material';
import {
  DesktopDatePicker,
  DesktopTimePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useContext, useEffect, useState } from 'react';

import {
  createBlockage,
  deleteBlockage,
  editBlockage,
} from '../../../api/calendarBlockages';
import { BlockageData } from '../../../api/calendarBlockages/types';
import { StatusCode } from '../../../api/enumerations';
import { InspectionAgendaData } from '../../../api/inspections/types';
import { UserData } from '../../../api/users/types';
import {
  menuItemDefaultStyle,
  StyledTextField,
} from '../../../components/UI/Typography';
import { Constants } from '../../../constants/agenda';
import { IconCloseMS } from '../../../constants/icons';
import { GlobalContext } from '../../../context/global';
import { formatTime } from '../../../helpers';
import {
  defaultTimezone,
  fromWallClock,
  toWallClock,
} from '../../../helpers/datetime';
import useErrorMessage from '../../../hooks/useErrorMessage';
import {
  ButtonsSpace,
  CloseIcon,
  DialogButton,
  DialogContainer,
  DialogTitle,
  FilledButton,
  OutlinedButton,
  StyledDialog,
} from './styles';

export interface IEventData extends InspectionAgendaData {
  color: string;
}

interface BlockAgendaDialogProps {
  usersData: UserData[];
  open: boolean;
  setOpen: (value: boolean) => void;
  blockage?: IEventData;
  updateAgenda: () => void;
}
export default function BlockAgendaDialog({
  usersData,
  open,
  setOpen,
  blockage,
  updateAgenda,
}: BlockAgendaDialogProps): JSX.Element {
  const [user, setUser] = useState<number>(usersData[0].id);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [startTime, setStartTime] = useState<Dayjs>(dayjs());
  const [endTime, setEndTime] = useState<Dayjs>(dayjs());
  const [formattedStartHour, setFormattedStartHour] = useState('');
  const [formattedEndHour, setFormattedEndHour] = useState('');
  const [observation, setObservation] = useState('');
  const [blockageId, setBlockageId] = useState<number>();

  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);

  const { getErrorMessage } = useErrorMessage();

  const handleOpen = (): void => setOpen(true);
  const handleClose = (): void => setOpen(false);

  const handleStartTime = (newValue: Dayjs | null): void => {
    if (newValue) {
      const endTime = dayjs(newValue).add(1, 'hours');
      setStartTime(newValue);
      setEndTime(endTime);
      setFormattedStartHour(formatTime(newValue));
      setFormattedEndHour(formatTime(endTime));
    }
  };

  const handleEndTime = (newValue: Dayjs | null): void => {
    if (newValue) {
      setEndTime(newValue);
      setFormattedEndHour(formatTime(newValue));
    }
  };

  const handleBlockage = async (): Promise<void> => {
    if (!startDate || !endDate || !formattedStartHour || !formattedEndHour) {
      setSnackbarMessage('Os campos de data e hora são obrigatórios.');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    // A blockage runs on the company's clock, not the browser's
    const timezone = blockage?.timezone ?? defaultTimezone;
    const blockageData: BlockageData = {
      start_at: fromWallClock(
        dayjs(`${dayjs(startDate).format('YYYY-MM-DD')}T${formattedStartHour}`),
        timezone
      ),
      finish_at: fromWallClock(
        dayjs(`${dayjs(endDate).format('YYYY-MM-DD')}T${formattedEndHour}`),
        timezone
      ),
      observations: observation,
    };

    if (!blockageId) blockageData.user_id = user;

    if (blockageId) {
      try {
        const response = await editBlockage(blockageData, blockageId);

        if (response.detail.description) {
          throw new Error(response.detail.description);
        }

        if (response.detail.status_code !== StatusCode.OK) {
          throw new Error('Algo deu errado, tente novamente.');
        }

        updateAgenda();
        setSnackbarMessage('Bloqueio editado com sucesso!');
        setErrorMessage(false);
        setOpenSnackbar(true);
        handleClose();
      } catch (error) {
        setSnackbarMessage(getErrorMessage(error));
        setErrorMessage(true);
        setOpenSnackbar(true);
      }
    } else {
      try {
        const response = await createBlockage(blockageData);

        if (response.detail.description) {
          throw new Error(response.detail.description);
        }

        if (response.detail.status_code !== StatusCode.OK) {
          throw new Error('Algo deu errado, tente novamente.');
        }

        updateAgenda();
        setSnackbarMessage('Bloqueio realizado com sucesso!');
        setErrorMessage(false);
        setOpenSnackbar(true);
        handleClose();
      } catch (error) {
        setSnackbarMessage(getErrorMessage(error));
        setErrorMessage(true);
        setOpenSnackbar(true);
      }
    }
  };

  useEffect(() => {
    if (blockage) {
      const startDate = toWallClock(blockage.start_at_dt, blockage.timezone);
      const endDate = toWallClock(blockage.finish_at_dt, blockage.timezone);

      setBlockageId(blockage.id);
      setUser(blockage.user.id);
      setStartDate(startDate);
      setEndDate(endDate);
      setStartTime(startDate);
      setEndTime(endDate);
      setFormattedStartHour(formatTime(startDate));
      setFormattedEndHour(formatTime(endDate));
      setObservation(blockage.observations || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockage]);

  const handleDeleteBlockage = async (): Promise<void> => {
    if (!blockageId) {
      setSnackbarMessage('Algo deu errado, tente novamente.');
      setOpenSnackbar(true);
      setErrorMessage(true);
      handleClose();
      return;
    }

    try {
      const response = await deleteBlockage(blockageId);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      updateAgenda();
      setSnackbarMessage('Bloqueio removido com sucesso!');
      setErrorMessage(false);
      setOpenSnackbar(true);
      handleClose();
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    }
  };

  return (
    <>
      {!blockage && (
        <DialogButton onClick={handleOpen}>
          {Constants.blockAgenda}
        </DialogButton>
      )}
      <StyledDialog open={open} onClose={handleClose}>
        <DialogContainer>
          <CloseIcon onClick={handleClose}>{IconCloseMS}</CloseIcon>
          <DialogTitle>{Constants.blockAgenda}</DialogTitle>
          <StyledTextField
            select
            label={Constants.user}
            value={user}
            disabled={!!blockageId}
            onChange={(e) => setUser(Number(e.target.value))}
          >
            {usersData.map((option) => (
              <MenuItem
                key={option.id}
                value={option.id}
                sx={menuItemDefaultStyle}
              >
                {option.name}
              </MenuItem>
            ))}
          </StyledTextField>
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  label={Constants.startDate}
                  value={startDate}
                  format="DD-MM-YYYY"
                  disablePast
                  slots={{
                    textField:
                      StyledTextField as React.ComponentType<TextFieldProps>,
                  }}
                  onChange={(e) => setStartDate(e)}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopTimePicker
                  label={Constants.start}
                  value={startTime}
                  ampm={false}
                  slots={{
                    textField:
                      StyledTextField as React.ComponentType<TextFieldProps>,
                  }}
                  onChange={handleStartTime}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={8}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  label={Constants.endDate}
                  value={endDate}
                  format="DD-MM-YYYY"
                  disablePast
                  slots={{
                    textField:
                      StyledTextField as React.ComponentType<TextFieldProps>,
                  }}
                  onChange={(e) => setEndDate(e)}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopTimePicker
                  label={Constants.end}
                  value={endTime}
                  ampm={false}
                  slots={{
                    textField:
                      StyledTextField as React.ComponentType<TextFieldProps>,
                  }}
                  onChange={handleEndTime}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
          <StyledTextField
            multiline
            rows={4}
            label={Constants.observation}
            color="primary"
            placeholder={Constants.addObs}
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
          />
          <ButtonsSpace>
            <OutlinedButton
              onClick={blockageId ? handleDeleteBlockage : handleClose}
            >
              {blockageId ? Constants.deleteBlock : Constants.cancel}
            </OutlinedButton>
            <FilledButton onClick={handleBlockage}>
              {blockageId ? Constants.editBlock : Constants.createBlock}
            </FilledButton>
          </ButtonsSpace>
        </DialogContainer>
      </StyledDialog>
    </>
  );
}
